import { plateFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  IconButton,
  Checkbox,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Collapse,
  Chip,
  Autocomplete,
  FormControlLabel,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventIcon from '@mui/icons-material/Event';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PICKER_MINUTES, belongsInSidebar } from './calendarConstants';
import { useUserStore } from '../../store';
import { can } from '../../utils/access';
import useCalendarColors from './hooks/useCalendarColors';
import useCalendarState from './hooks/useCalendarState';
import { format, setTimeOfDay, workScheduleHoursForDate } from './hooks/dateUtils';
import { detectConflicts } from './hooks/conflictUtils';
import { useVehicleLookup } from './hooks/useVehicleLookup';
import SearchIcon from '@mui/icons-material/Search';
import ExtraLookupConfirmDialog, { shouldSkipExtraLookupConfirm } from '../vehicles/ExtraLookupConfirmDialog';
import HelpButton from '../shared/HelpButton';
import CustomerVehicleChips from '../shared/CustomerVehicleChips';
import ScheduleReminderSection, { useScheduleReminders } from '../shared/ScheduleReminderSection';
import BookingNotifyChoice from '../shared/BookingNotifyChoice';
import { statusConfig } from '../shared/StatusBadge';
import NewCustomerForm, {
  EMPTY_NEW_CUSTOMER,
  isNewCustomerValid,
  isNewCustomerIncomplete,
} from '../shared/NewCustomerForm';
import { customerLabel } from '../../utils/customer';

const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#ef4444', bg: '#fee2e2' },
  ordered: { color: '#d97706', bg: '#fef3c7' },
  arrived: { color: '#059669', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

function ScheduleDialog({ t }) {
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const canSchedule = can(currentOrganization, 'work_orders.schedule');
  const {
    scheduleDialogOpen,
    scheduleDialogMode,
    closeScheduleDialog,
    selectedMaintenance,
    selectedTimeSlot,
    employees,
    serviceBays,
    unscheduledMaintenances,
    addEvent,
    updateEvent,
    removeEvent,
    removeFromUnscheduled,
    addToUnscheduled,
    editingEvent,
    workSchedule,
    events, // All scheduled events for conflict detection
    unavailabilities, // Employee unavailabilities for conflict detection
    updateEventsByMaintenance,
    updateUnscheduledMaintenance,
  } = useCalendarState();
  // A colour tag belongs to the work order, so picking one here repaints every card of it.
  const { activeTags: activeColorTags } = useCalendarColors();
  const canTag = can(currentOrganization, 'work_orders.edit');

  // Default start without a clicked slot: today's work day start (the week's bounds on a closed day)
  const { startHour: startHourInt } = workScheduleHoursForDate(workSchedule, new Date());

  const isEditMode = !!editingEvent;

  const [formData, setFormData] = useState({
    maintenanceId: '',
    startDateTime: null,
    endDateTime: null,
    serviceBayId: '',
    employeeIds: [],
  });
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveErrorRef = useRef(null);
  // The error alert sits at the top of the scrolling content, the save button at the bottom
  useEffect(() => {
    if (saveError) saveErrorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [saveError]);
  // Booking-linked work orders: tell the customer about the moved time? Reset on every open.
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  // Mode toggle state ('new' or 'existing')
  const [mode, setMode] = useState('existing');

  // Customer reminder / notification state (shared section + save helper).
  // In 'new' mode there is no maintenance yet — contact info comes from the
  // customer picker / new-customer form via contactOverride below.
  const reminderMaintenanceId = isEditMode
    ? editingEvent?.maintenance_id
    : mode === 'existing'
      ? formData.maintenanceId || null
      : null;
  const reminders = useScheduleReminders({
    open: scheduleDialogOpen,
    maintenanceId: reminderMaintenanceId,
    scheduleId: isEditMode ? editingEvent?.id : null,
  });

  // Customers list for new maintenance
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);

  // Maintenance search for "link existing" Autocomplete
  const [maintenanceSearchTerm, setMaintenanceSearchTerm] = useState('');
  const [maintenanceOptions, setMaintenanceOptions] = useState([]);
  const [maintenanceSearchLoading, setMaintenanceSearchLoading] = useState(false);
  const [selectedMaintenanceObj, setSelectedMaintenanceObj] = useState(null);
  const [includeScheduled, setIncludeScheduled] = useState(false);

  // Customer mode toggle for new maintenance
  const [customerMode, setCustomerMode] = useState('existing');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customerAutoFilledRef = useRef('none'); // 'none' | 'auto' | 'manual'
  const [newCustomer, setNewCustomer] = useState({ ...EMPTY_NEW_CUSTOMER });

  // New maintenance form data
  const [newMaintenanceData, setNewMaintenanceData] = useState({
    registrationNumber: '',
    description: '',
    vehicle: null,
  });

  // Callback for when vehicle lookup finds a vehicle
  const handleVehicleFound = useCallback((vehicleData) => {
    setNewMaintenanceData((prev) => ({
      ...prev,
      registrationNumber: vehicleData.registration_number || prev.registrationNumber,
      vehicle: vehicleData,
    }));
  }, []);

  // Callback for when vehicle lookup finds a previous customer
  const handleCustomerFound = useCallback((customerData) => {
    if (customerMode !== 'existing') return; // Don't prefill when creating new customer
    if (customerAutoFilledRef.current === 'manual') return; // Don't override manual selection
    setSelectedCustomer(customerData);
    customerAutoFilledRef.current = 'auto';
  }, [customerMode]);

  // Vehicle lookup hook
  const {
    lookupLoading,
    lookupError,
    vehicleLookedUp,
    showTrialInfo,
    showNonFinnishInfo,
    quotaInfo,
    quotaExceeded,
    requiresConfirmation,
    trialRestricted,
    handleRegistrationChange: hookHandleRegistrationChange,
    lookupVehicle,
    lookupWithConfirm,
    interruptLookup,
    cancelPendingLookup,
    resetLookup,
    fetchQuota,
  } = useVehicleLookup({ t, onVehicleFound: handleVehicleFound, onCustomerFound: handleCustomerFound });

  const [extraLookupDialogOpen, setExtraLookupDialogOpen] = useState(false);

  // Wrapper for registration change that also updates local form state
  const handleRegistrationChange = useCallback((value) => {
    const cleanValue = hookHandleRegistrationChange(value);
    setNewMaintenanceData((prev) => ({ ...prev, registrationNumber: cleanValue, vehicle: null }));
    // Clear auto-filled customer when reg changes (matches AddMaintenanceDialog pattern)
    if (customerAutoFilledRef.current === 'auto') {
      setSelectedCustomer(null);
      customerAutoFilledRef.current = 'none';
    }
  }, [hookHandleRegistrationChange]);

  // Vehicle typeahead
  const [vehicleTypeaheadOptions, setVehicleTypeaheadOptions] = useState([]);
  const [vehicleTypeaheadLoading, setVehicleTypeaheadLoading] = useState(false);
  const vehicleTypeaheadTimerRef = useRef(null);
  const vehicleTypeaheadOpenRef = useRef(false);

  useEffect(() => {
    if (vehicleLookedUp) {
      setVehicleTypeaheadOptions([]);
    }
  }, [vehicleLookedUp]);

  const searchVehicleTypeahead = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setVehicleTypeaheadOptions([]);
      return;
    }
    setVehicleTypeaheadLoading(true);
    try {
      const response = await axios.get(`/api/vehicles/typeahead/?search=${encodeURIComponent(query)}`);
      setVehicleTypeaheadOptions(response.data || []);
    } catch (err) {
      setVehicleTypeaheadOptions([]);
    } finally {
      setVehicleTypeaheadLoading(false);
    }
  }, []);

  const handleVehicleTypeaheadSelect = useCallback((option) => {
    setVehicleTypeaheadOptions([]);
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    cancelPendingLookup();
    lookupVehicle(option.registration_number);
    setNewMaintenanceData((prev) => ({ ...prev, registrationNumber: option.registration_number }));
  }, [cancelPendingLookup, lookupVehicle]);

  const handleRegistrationChangeWithTypeahead = useCallback((value) => {
    handleRegistrationChange(value);
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    if (value.length >= 2) {
      vehicleTypeaheadTimerRef.current = setTimeout(() => {
        searchVehicleTypeahead(value);
      }, 500);
    } else {
      setVehicleTypeaheadOptions([]);
    }
  }, [handleRegistrationChange, searchVehicleTypeahead]);

  // Handle explicit lookup button click
  const handleLookupClick = useCallback(() => {
    const regNumber = newMaintenanceData.registrationNumber;
    if (regNumber) {
      lookupVehicle(regNumber);
    }
  }, [newMaintenanceData.registrationNumber, lookupVehicle]);

  // Handle quota exceeded - open confirmation dialog or auto-confirm
  useEffect(() => {
    if (quotaExceeded && requiresConfirmation) {
      if (shouldSkipExtraLookupConfirm()) {
        lookupWithConfirm(newMaintenanceData.registrationNumber);
      } else {
        setExtraLookupDialogOpen(true);
      }
    }
  }, [quotaExceeded, requiresConfirmation, newMaintenanceData.registrationNumber, lookupWithConfirm]);

  // Fetch customers when dialog opens or when search term changes
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomerLoading(true);
      try {
        const params = customerSearchTerm
          ? { search: customerSearchTerm, typeahead: 'true' }
          : { page_size: 50 };
        const response = await axios.get('/api/customers/', { params });
        // Handle paginated response
        const customersData = response.data.results || response.data;
        setCustomers(Array.isArray(customersData) ? customersData : []);
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        setCustomerLoading(false);
      }
    };

    if (scheduleDialogOpen) {
      const debounceTimer = setTimeout(fetchCustomers, customerSearchTerm ? 300 : 0);
      return () => clearTimeout(debounceTimer);
    }
  }, [scheduleDialogOpen, customerSearchTerm]);

  // Fetch schedulable maintenances for "link existing" typeahead
  useEffect(() => {
    const fetchSchedulable = async () => {
      setMaintenanceSearchLoading(true);
      try {
        const params = {};
        if (maintenanceSearchTerm) params.search = maintenanceSearchTerm;
        if (includeScheduled) params.include_scheduled = 'true';
        const response = await axios.get('/api/scheduling/calendar/schedulable/', { params });
        setMaintenanceOptions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setMaintenanceOptions([]);
      } finally {
        setMaintenanceSearchLoading(false);
      }
    };

    if (scheduleDialogOpen && mode === 'existing') {
      const debounceTimer = setTimeout(fetchSchedulable, maintenanceSearchTerm ? 300 : 0);
      return () => clearTimeout(debounceTimer);
    }
  }, [scheduleDialogOpen, mode, maintenanceSearchTerm, includeScheduled]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (scheduleDialogOpen) {
      setNotifyCustomer(true);
      let initialData;

      if (isEditMode && editingEvent) {
        // Edit mode - pre-fill with existing event data
        initialData = {
          maintenanceId: editingEvent.maintenance_id,
          startDateTime: new Date(editingEvent.start_time),
          endDateTime: new Date(editingEvent.end_time),
          serviceBayId: editingEvent.service_bay_id || '',
          employeeIds: editingEvent.employee_ids || [],
        };
      } else {
        // Create mode - use selected time slot from calendar
        let startDateTime, endDateTime;

        if (selectedTimeSlot) {
          const startDate = selectedTimeSlot.startDate || selectedTimeSlot.date;
          const endDate = selectedTimeSlot.endDate || selectedTimeSlot.date;

          // Build start datetime
          startDateTime = new Date(startDate);
          startDateTime.setHours(Math.floor(selectedTimeSlot.startHour), Math.round((selectedTimeSlot.startHour % 1) * 60), 0, 0);

          // Build end datetime
          endDateTime = new Date(endDate);
          endDateTime.setHours(Math.floor(selectedTimeSlot.endHour), Math.round((selectedTimeSlot.endHour % 1) * 60), 0, 0);
        } else {
          // Default: today, work hours
          startDateTime = new Date();
          setTimeOfDay(startDateTime, startHourInt);
          endDateTime = new Date(startDateTime);
          setTimeOfDay(endDateTime, startHourInt + 2);
        }

        initialData = {
          maintenanceId: selectedMaintenance?.id || '',
          startDateTime,
          endDateTime,
          serviceBayId: selectedTimeSlot?.resourceType === 'bay' ? selectedTimeSlot.resourceId : '',
          employeeIds: selectedTimeSlot?.resourceType === 'employee' ? [selectedTimeSlot.resourceId] : [],
        };
      }

      setFormData(initialData);
      setConflicts([]);
      setDeleteConfirm(false);

      // Set mode from store (preselected from context menu)
      setMode(scheduleDialogMode);

      // Reset new maintenance data
      setNewMaintenanceData({
        registrationNumber: '',
        description: '',
        vehicle: null,
      });
      setSelectedCustomer(null);
      setCustomerMode('existing');
      customerAutoFilledRef.current = 'none';
      setNewCustomer({ ...EMPTY_NEW_CUSTOMER });
      resetLookup();
      setSaveError(null);
      if (vehicleTypeaheadTimerRef.current) {
        clearTimeout(vehicleTypeaheadTimerRef.current);
      }
      setVehicleTypeaheadOptions([]);

      // Reset maintenance search state
      setMaintenanceSearchTerm('');
      setMaintenanceOptions([]);
      setSelectedMaintenanceObj(selectedMaintenance || null);
      setIncludeScheduled(false);
    }
  }, [scheduleDialogOpen, selectedMaintenance, selectedTimeSlot, isEditMode, editingEvent, startHourInt, scheduleDialogMode, resetLookup]);

  // Conflict detection - check for overlapping schedules
  useEffect(() => {
    if (!scheduleDialogOpen || !formData.startDateTime || !formData.endDateTime) {
      setConflicts([]);
      return;
    }

    const newConflicts = detectConflicts({
      plannedStart: formData.startDateTime,
      plannedEnd: formData.endDateTime,
      employeeIds: formData.employeeIds,
      serviceBayId: formData.serviceBayId,
      existingEvents: events,
      unavailabilities,
      excludeEventId: isEditMode && editingEvent ? editingEvent.id : null,
      employees,
      serviceBays,
      t,
    });

    setConflicts(newConflicts);
  }, [scheduleDialogOpen, formData.startDateTime, formData.endDateTime, formData.employeeIds, formData.serviceBayId, events, unavailabilities, isEditMode, editingEvent, employees, serviceBays, t]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Ensure end time is always after start time (minimum 5 minutes)
      if (field === 'startDateTime' && value && newData.endDateTime) {
        const minEnd = new Date(value.getTime() + PICKER_MINUTES * 60 * 1000);
        if (newData.endDateTime <= value) {
          newData.endDateTime = minEnd;
        }
      }
      if (field === 'endDateTime' && value && newData.startDateTime) {
        const minEnd = new Date(newData.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000);
        if (value < minEnd) {
          newData.endDateTime = minEnd;
        }
      }

      return newData;
    });
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData((prev) => ({
      ...prev,
      employeeIds: prev.employeeIds.includes(employeeId)
        ? prev.employeeIds.filter((id) => id !== employeeId)
        : [...prev.employeeIds, employeeId],
    }));
  };


  const handleSubmit = async () => {
    // Cancel any pending vehicle lookup - user wants to proceed now
    cancelPendingLookup();
    setSaveError(null);

    // A new customer with nothing to identify it by would be skipped below and its details lost: refuse
    if (!isEditMode && mode === 'new' && customerMode === 'new' && isNewCustomerIncomplete(newCustomer)) {
      setSaveError(t('customer.newIdentityMissing'));
      return;
    }

    setLoading(true);

    try {
      const startDateTime = formData.startDateTime;
      const endDateTime = formData.endDateTime;

      if (isEditMode) {
        // SAFETY CHECK: Never proceed without a valid maintenance ID
        if (!editingEvent.maintenance_id) {
          setSaveError(t('Cannot update: invalid event data'));
          setLoading(false);
          return;
        }

        // Update existing schedule. employee_ids is this slot's own team; the work order
        // team follows on the backend (scheduling/services/assignment.py).
        const patchResponse = await axios.patch(`/api/scheduling/schedules/${editingEvent.id}/`, {
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
          ...(editingEvent.has_active_booking ? { notify_customer: notifyCustomer } : {}),
        });

        // Persist customer reminder/notification choices (non-fatal on failure)
        await reminders.save(editingEvent.id);

        // Update local state with local_* fields for multi-day rendering
        updateEvent(editingEvent.id, {
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          employee_ids: patchResponse.data?.employee_ids ?? formData.employeeIds,
          service_bay_id: formData.serviceBayId || null,
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });
      } else if (mode === 'new') {
        // Create new customer first if needed
        let customerId = selectedCustomer?.id || null;
        if (customerMode === 'new' && isNewCustomerValid(newCustomer)) {
          try {
            const customerResponse = await axios.post('/api/customers/', newCustomer);
            customerId = customerResponse.data.id;
          } catch (customerErr) {
            const errorMessage = customerErr.response?.data?.non_field_errors?.[0] ||
              customerErr.response?.data?.detail ||
              Object.values(customerErr.response?.data || {}).flat().join(', ') ||
              t('Failed to create customer');
            setSaveError(errorMessage);
            setLoading(false);
            return;
          }
        }

        // Create new maintenance
        const maintenancePayload = {
          vehicle: newMaintenanceData.vehicle || {
            registration_number: (newMaintenanceData.registrationNumber || '').toUpperCase(),
          },
          description: newMaintenanceData.description,
          state: 'planned',
        };

        // Add customer if selected
        if (customerId) {
          maintenancePayload.customer_id = customerId;
        }

        const maintenanceResponse = await axios.post('/api/maintenances/', maintenancePayload);
        const newMaintenanceId = maintenanceResponse.data.id;

        // Create schedule for the new maintenance; employee_ids is the slot's team
        const scheduleResponse = await axios.post('/api/scheduling/schedules/', {
          maintenance: newMaintenanceId,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
        });

        // Persist customer reminder/notification choices (non-fatal on failure)
        await reminders.save(scheduleResponse.data.id);

        // Update local state with local_* fields for multi-day rendering
        let customerName = null;
        if (customerMode === 'new' && customerId) {
          // Same label as the calendar API gives after a reload: the name, else phone or email
          customerName = (newCustomer.type === 'company'
            ? newCustomer.company_name
            : `${newCustomer.first_name} ${newCustomer.last_name}`.trim()) || newCustomer.phone || newCustomer.email;
        } else {
          customerName = customerLabel(selectedCustomer) || null;
        }
        addEvent({
          id: scheduleResponse.data.id,
          maintenance_id: newMaintenanceId,
          vehicle_registration: (newMaintenanceData.registrationNumber || '').toUpperCase(),
          description: newMaintenanceData.description || '',
          state: 'planned',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          employee_ids: scheduleResponse.data?.employee_ids ?? formData.employeeIds,
          service_bay_id: formData.serviceBayId || null,
          customer_name: customerName,
          parts_order_status: maintenanceResponse.data.parts_order_status || null,
          // Local timezone fields for multi-day rendering
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });
      } else {
        // Link existing maintenance - create a new slot with its own team. An empty
        // selection lets the backend copy the work order team onto the slot.
        const scheduleResponse = await axios.post('/api/scheduling/schedules/', {
          maintenance: formData.maintenanceId,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          service_bay: formData.serviceBayId || null,
          employee_ids: formData.employeeIds,
        });

        // Persist customer reminder/notification choices (non-fatal on failure)
        await reminders.save(scheduleResponse.data.id);

        // Update local state with local_* fields for multi-day rendering
        const maintenance = selectedMaintenanceObj;
        addEvent({
          id: scheduleResponse.data.id,
          maintenance_id: formData.maintenanceId,
          vehicle_registration: maintenance?.vehicle_registration || '',
          description: maintenance?.description || '',
          state: maintenance?.state || 'planned',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          employee_ids: scheduleResponse.data?.employee_ids ?? formData.employeeIds,
          service_bay_id: formData.serviceBayId || null,
          customer_name: maintenance?.customer_name || null,
          parts_order_status: maintenance?.parts_order_status || null,
          // Local timezone fields for multi-day rendering
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });

        // Only remove from unscheduled sidebar if it was there
        if (unscheduledMaintenances.some((m) => m.id === formData.maintenanceId)) {
          removeFromUnscheduled(formData.maintenanceId);
        }
      }

      closeScheduleDialog();
    } catch (error) {
      console.error('Error saving schedule:', error);
      const data = error.response?.data;
      let message;
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (data.detail) {
          message = data.detail;
        } else if (data.message) {
          message = data.message;
        } else if (data.non_field_errors) {
          message = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(', ') : data.non_field_errors;
        } else {
          const fieldErrors = Object.entries(data)
            .map(([field, errors]) => {
              const errorMsg = Array.isArray(errors) ? errors.join(', ') : String(errors);
              return `${field}: ${errorMsg}`;
            })
            .join('; ');
          message = fieldErrors || t('Failed to save. Please try again.');
        }
      } else {
        message = error.message || t('Failed to save. Please try again.');
      }
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    if (!editingEvent) return;

    setLoading(true);
    setSaveError(null);
    try {
      // Delete only the schedule - employee assignments should be preserved
      await axios.delete(`/api/scheduling/schedules/${editingEvent.id}/`);

      // Update local state - remove from events
      removeEvent(editingEvent.id);

      // Only add to unscheduled if no other schedules remain for this maintenance
      const remainingForMaintenance = events.filter(
        (e) => e.maintenance_id === editingEvent.maintenance_id && e.id !== editingEvent.id
      );
      if (remainingForMaintenance.length === 0 && belongsInSidebar(editingEvent.state)) {
        addToUnscheduled({
          id: editingEvent.maintenance_id,
          vehicle_registration: editingEvent.vehicle_registration,
          customer_name: editingEvent.customer_name,
          description: editingEvent.description,
          priority: 'normal',
          created_at: new Date().toISOString(),
        });
      }

      closeScheduleDialog();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      const message = error.response?.data?.detail || error.response?.data?.message || t('Failed to delete. Please try again.');
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={scheduleDialogOpen}
      onClose={closeScheduleDialog}
      maxWidth="sm"
      fullWidth
      data-testid="schedule-dialog"
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {isEditMode ? t('Edit schedule') : t('Schedule maintenance')}
        </Typography>
        <IconButton onClick={closeScheduleDialog} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Save Error */}
        {saveError && (
          <Alert
            ref={saveErrorRef}
            severity="error"
            onClose={() => setSaveError(null)}
            sx={{ mb: 2.5, borderRadius: '8px' }}
          >
            {saveError}
          </Alert>
        )}

        {/* Conflict Warning */}
        {conflicts.filter((c) => c.type !== 'work_hours').length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2.5, borderRadius: '8px' }}
          >
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t('Conflict detected')}</Typography>
            {conflicts.filter((c) => c.type !== 'work_hours').map((conflict, idx) => (
              <Typography key={idx} sx={{ fontSize: '13px' }}>
                {conflict.message}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Work hours info */}
        {conflicts.filter((c) => c.type === 'work_hours').length > 0 && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: '8px' }}>
            {conflicts.filter((c) => c.type === 'work_hours').map((conflict, idx) => (
              <Typography key={idx} sx={{ fontSize: '13px' }}>
                {conflict.message}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Linked booking: the slot change syncs it; the checkbox decides about the message */}
        {isEditMode && editingEvent?.has_active_booking && (
          <BookingNotifyChoice
            checked={notifyCustomer}
            onChange={setNotifyCustomer}
            showChoice={
              !formData.startDateTime ||
              new Date(editingEvent.start_time).getTime() !== formData.startDateTime.getTime()
            }
            sx={{ mb: 2.5 }}
          />
        )}

        {/* Mode Toggle (only in create mode, not edit mode) */}
        {!isEditMode && (
          <Box sx={{ mb: 2.5 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, newMode) => newMode && setMode(newMode)}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 500,
                  py: 1,
                  '&.Mui-selected': {
                    bgcolor: 'secondary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="new" data-testid="schedule-dialog-mode-new">
                <AddIcon sx={{ mr: 1, fontSize: 18 }} />
                {t('Create new maintenance')}
              </ToggleButton>
              <ToggleButton value="existing" data-testid="schedule-dialog-mode-existing">
                <LinkIcon sx={{ mr: 1, fontSize: 18 }} />
                {t('Link existing')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Maintenance Section */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Maintenance')}</Typography>
          {isEditMode ? (
            // Edit mode - show existing event info with more details
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: '10px',
                border: 1,
                borderColor: 'divider',
              }}
            >
              {/* Header with registration and link */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  sx={{
                    ...plateFont,
                    fontWeight: 700,
                    fontSize: '16px',
                    color: 'text.primary',
                  }}
                >
                  {editingEvent?.vehicle_registration}
                </Typography>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    closeScheduleDialog();
                    navigate(`/maintenances/${editingEvent?.maintenance_id}`);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    color: 'secondary.main',
                    minWidth: 'auto',
                    p: 0.5,
                    '&:hover': { bgcolor: 'rgba(49, 130, 206, 0.08)' },
                  }}
                >
                  {t('View details')}
                </Button>
              </Box>

              {/* Description */}
              {editingEvent?.description && (
                <Typography sx={{ fontSize: '13px', color: 'text.primary', mb: 1 }}>
                  {editingEvent.description}
                </Typography>
              )}

              {/* Customer */}
              {editingEvent?.customer_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {editingEvent.customer_name}
                  </Typography>
                </Box>
              )}

              {/* State chip */}
              {editingEvent?.state && (
                <Chip
                  size="small"
                  label={t(editingEvent.state)}
                  sx={{
                    fontSize: '11px',
                    height: 22,
                    bgcolor: editingEvent.state === 'planned' ? 'info.lighter' :
                             editingEvent.state === 'in_progress' ? 'warning.lighter' :
                             editingEvent.state === 'completed' ? 'success.lighter' : 'grey.200',
                    color: editingEvent.state === 'planned' ? 'info.main' :
                           editingEvent.state === 'in_progress' ? 'warning.main' :
                           editingEvent.state === 'completed' ? 'success.main' : 'text.secondary',
                  }}
                />
              )}
              {/*
                Shown whenever the organization tracks parts orders, also when the work order
                has no status yet: work orders born outside the create endpoint (online
                bookings above all) used to hide the control completely.
              */}
              {/* Colour tag (väritunniste) */}
              {canTag && activeColorTags.length > 0 && editingEvent?.maintenance_id && (
                <Select
                  size="small"
                  displayEmpty
                  data-testid="schedule-dialog-color-tag-select"
                  value={editingEvent.color_tag_id ?? ''}
                  onChange={async (e) => {
                    const nextId = e.target.value === '' ? null : e.target.value;
                    try {
                      await axios.post(`/api/maintenances/${editingEvent.maintenance_id}/set-color-tag/`, {
                        color_tag: nextId,
                      });
                      updateEventsByMaintenance(editingEvent.maintenance_id, { color_tag_id: nextId });
                      updateUnscheduledMaintenance(editingEvent.maintenance_id, { color_tag_id: nextId });
                      useCalendarState.setState({
                        editingEvent: { ...editingEvent, color_tag_id: nextId },
                      });
                    } catch (err) {
                      console.error('Failed to set color tag:', err);
                    }
                  }}
                  sx={{ ml: 1, minWidth: 150, height: 26, fontSize: '11px', '& .MuiSelect-select': { py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 } }}
                  renderValue={(val) => {
                    const tag = activeColorTags.find((candidate) => candidate.id === val);
                    return tag ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '2px', bgcolor: tag.color }} />
                        <span>{tag.name}</span>
                      </Box>
                    ) : (
                      <span>{t('calendar.colorTags.selectLabel')}</span>
                    );
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '12px' }}>
                    {t('calendar.colorTags.selectNone')}
                  </MenuItem>
                  {activeColorTags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id} sx={{ fontSize: '12px' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '2px', bgcolor: tag.color, mr: 1 }} />
                      {tag.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
              {currentOrganization?.parts_order_tracking_enabled && editingEvent?.maintenance_id && (
                <Select
                  size="small"
                  displayEmpty
                  value={editingEvent.parts_order_status || ''}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    try {
                      await axios.post(`/api/maintenances/${editingEvent.maintenance_id}/set-parts-order-status/`, { status: newStatus });
                      updateEvent(editingEvent.id, { parts_order_status: newStatus });
                      useCalendarState.setState({ editingEvent: { ...editingEvent, parts_order_status: newStatus } });
                    } catch (err) {
                      console.error('Failed to set parts order status:', err);
                    }
                  }}
                  sx={{ ml: 1, minWidth: 150, height: 26, fontSize: '11px', '& .MuiSelect-select': { py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 } }}
                  renderValue={(val) => {
                    const cfg = PARTS_ORDER_CONFIG[val];
                    return cfg ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.color }} />
                        <span>{t(`parts_order.${val}_short`)}</span>
                      </Box>
                    ) : (
                      <span>{t('parts_order.status')}</span>
                    );
                  }}
                >
                  {['not_ordered', 'ordered', 'arrived', 'not_needed'].map((s) => (
                    <MenuItem key={s} value={s} sx={{ fontSize: '12px' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: PARTS_ORDER_CONFIG[s].color, mr: 1 }} />
                      {t(`parts_order.${s}_short`)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          ) : mode === 'new' ? (
            // Create new maintenance - show registration and description fields
            <>
              {/* Registration Number */}
              <Autocomplete
                key="registration-typeahead"
                freeSolo
                open={vehicleTypeaheadOptions.length > 0 && !vehicleLookedUp}
                options={vehicleTypeaheadOptions}
                getOptionLabel={(option) => typeof option === 'string' ? option : (option.registration_number || '')}
                filterOptions={(x) => x}
                loading={vehicleTypeaheadLoading}
                onOpen={() => { vehicleTypeaheadOpenRef.current = true; }}
                onClose={() => { vehicleTypeaheadOpenRef.current = false; }}
                inputValue={newMaintenanceData.registrationNumber}
                onInputChange={(event, newValue, reason) => {
                  if (reason === 'input') {
                    handleRegistrationChangeWithTypeahead(newValue);
                  } else if (reason === 'clear') {
                    handleRegistrationChangeWithTypeahead('');
                  }
                }}
                onChange={(event, newValue, reason) => {
                  if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
                    handleVehicleTypeaheadSelect(newValue);
                  }
                }}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props;
                  return (
                    <Box component="li" key={option.id} {...restProps}>
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, ...plateFont }}
                        >
                          {option.registration_number}
                          {(option.manufacturer || option.model) && (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 1, fontFamily: 'inherit', fontWeight: 400 }}
                            >
                              {[option.manufacturer, option.model].filter(Boolean).join(' ')}
                              {option.year_model ? ` (${option.year_model})` : ''}
                            </Typography>
                          )}
                        </Typography>
                        {option.last_customer_name && (
                          <Typography variant="caption" color="text.secondary">
                            {option.last_customer_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    placeholder="ABC-123"
                    data-testid="schedule-registration-input"
                    fullWidth
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if ((newMaintenanceData.registrationNumber || '').length >= 3 && !vehicleLookedUp && !lookupLoading && !vehicleTypeaheadOpenRef.current) {
                          handleLookupClick();
                        }
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...plateFont,
                        fontSize: '14px',
                      },
                      endAdornment: (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {lookupLoading && (
                              <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                <Button
                                  size="small"
                                  onClick={interruptLookup}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                      color: 'text.primary',
                                      bgcolor: 'action.hover',
                                    },
                                  }}
                                >
                                  {t('Skip')}
                                </Button>
                              </>
                            )}
                            {!lookupLoading && !vehicleLookedUp && (newMaintenanceData.registrationNumber || '').length >= 3 && (
                              <Button
                                size="small"
                                onClick={handleLookupClick}
                                startIcon={<SearchIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1.5,
                                  py: 0.5,
                                  fontSize: '11px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t('vehicleLookup.button')}
                              </Button>
                            )}
                            {vehicleLookedUp && !lookupLoading && (
                              <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                            )}
                          </Box>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    }}
                  />
                )}
              />
              {/* Quota indicator */}
              {quotaInfo && !vehicleLookedUp && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  {quotaInfo.vehicle_data_provider === 'rekisterinumeroapi' ? (
                    <Typography variant="caption" color="success.main">
                      {t('vehicleLookup.fallbackProviderShort')}
                    </Typography>
                  ) : (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {t('vehicleLookup.quotaRemaining', {
                          remaining: Math.max(0, quotaInfo.remaining_lookups),
                          total: quotaInfo.included_lookups,
                        })}
                      </Typography>
                      <HelpButton text={t('vehicleLookup.quotaHelp')} size={16} />
                    </>
                  )}
                </Box>
              )}

              {/* Lookup Error */}
              <Collapse in={!!lookupError}>
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px' }}>
                  {lookupError}
                </Alert>
              </Collapse>

              {/* Trial Info Box - vehicle lookup quota exceeded or paid feature */}
              <Collapse in={showTrialInfo}>
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon />}
                  sx={{
                    mt: 1.5,
                    borderRadius: '10px',
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {t('Vehicle lookup is a paid feature')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                    {t('vehicleLookup.trialMessage')}
                  </Typography>
                </Alert>
              </Collapse>

              {/* Non-Finnish registration info */}
              <Collapse in={showNonFinnishInfo && !vehicleLookedUp}>
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon />}
                  sx={{ mt: 1.5, borderRadius: '10px' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '12px' }}>
                    {t('Vehicle lookup is only available for Finnish registration numbers (e.g. ABC-123). You can proceed with just the registration number.')}
                  </Typography>
                </Alert>
              </Collapse>

              {/* Vehicle Info (shown after successful lookup) */}
              <Collapse in={vehicleLookedUp && !!newMaintenanceData.vehicle?.manufacturer}>
                <Box
                  sx={{
                    mt: 1.5,
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {newMaintenanceData.vehicle?.manufacturer} {newMaintenanceData.vehicle?.model}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {newMaintenanceData.vehicle?.year_model && (
                      <Chip size="small" label={newMaintenanceData.vehicle.year_model} variant="outlined" />
                    )}
                    {newMaintenanceData.vehicle?.fuel && (
                      <Chip size="small" label={newMaintenanceData.vehicle.fuel} variant="outlined" />
                    )}
                    {newMaintenanceData.vehicle?.power_kw && (
                      <Chip size="small" label={`${newMaintenanceData.vehicle.power_kw} kW`} variant="outlined" />
                    )}
                  </Box>
                </Box>
              </Collapse>

              {/* Description */}
              <TextField
                placeholder={t('Description (optional)')}
                data-testid="schedule-description-input"
                value={newMaintenanceData.description}
                onChange={(e) => setNewMaintenanceData((prev) => ({ ...prev, description: e.target.value }))}
                fullWidth
                size="small"
                multiline
                rows={2}
                sx={{
                  mt: 1.5,
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />

              {/* Customer */}
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>
                  {t('Customer')} ({t('optional')})
                </Typography>

                {/* Customer Mode Toggle */}
                <ToggleButtonGroup
                  value={customerMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      setCustomerMode(newMode);
                      setSaveError(null);
                    }
                  }}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5 }}
                >
                  <ToggleButton value="existing" sx={{ textTransform: 'none', py: 0.5 }}>
                    <PersonSearchIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    {t('Existing customer')}
                  </ToggleButton>
                  <ToggleButton value="new" sx={{ textTransform: 'none', py: 0.5 }}>
                    <AddIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    {t('New customer')}
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Existing Customer Selection */}
                {customerMode === 'existing' && (
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => customerLabel(option)}
                    value={selectedCustomer}
                    onChange={(e, newValue) => { setSelectedCustomer(newValue); customerAutoFilledRef.current = newValue ? 'manual' : 'none'; }}
                    filterOptions={(x) => x}
                    onInputChange={(e, newInputValue, reason) => {
                      if (reason === 'input') {
                        setCustomerSearchTerm(newInputValue);
                      }
                    }}
                    loading={customerLoading}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoComplete="off"
                        placeholder={t('Search by name, email, phone, or business ID...')}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {customerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {option.type === 'company' ? (
                              <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            ) : (
                              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            )}
                            <Typography variant="body2">{customerLabel(option)}</Typography>
                          </Box>
                          {option.email && (
                            <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    noOptionsText={customerSearchTerm ? t('No customers found') : t('Type to search...')}
                  />
                )}

                {/* Customer's existing vehicles - shown only before a registration is entered */}
                {customerMode === 'existing' && selectedCustomer && !newMaintenanceData.registrationNumber && (
                  <CustomerVehicleChips
                    customerId={selectedCustomer?.id}
                    onSelect={handleVehicleTypeaheadSelect}
                  />
                )}

                {/* New Customer Form */}
                {customerMode === 'new' && (
                  <NewCustomerForm t={t} value={newCustomer} onChange={setNewCustomer} size="small" />
                )}
              </Box>
            </>
          ) : (
            // Link existing - typeahead search
            <>
            <Autocomplete
              key="maintenance-search"
              data-testid="schedule-maintenance-autocomplete"
              options={maintenanceOptions}
              getOptionLabel={(option) =>
                option ? `${option.work_number || ''} ${option.vehicle_registration || ''} ${option.customer_name || ''}`.trim() : ''
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(x) => x}
              loading={maintenanceSearchLoading}
              value={selectedMaintenanceObj}
              onChange={(event, newValue) => {
                setSelectedMaintenanceObj(newValue);
                handleFieldChange('maintenanceId', newValue?.id || '');
                // A new slot defaults to the work order's team: show that in the chips
                // when the user has not picked anyone yet.
                if (newValue && formData.employeeIds.length === 0 && (newValue.employee_ids || []).length > 0) {
                  handleFieldChange('employeeIds', [...newValue.employee_ids]);
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setMaintenanceSearchTerm(newInputValue);
                }
              }}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                const stateConf = statusConfig[option.state] || statusConfig.planned;
                const employeeNames = (option.employee_ids || [])
                  .map((id) => employees.find((e) => e.id === id)?.name)
                  .filter(Boolean);
                const isAlreadyScheduled = option.schedule_count > 0;
                return (
                  <Box component="li" key={option.id} {...restProps} sx={{ py: 1, px: 1.5 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                            #{option.work_number}
                          </Typography>
                          {isAlreadyScheduled && (
                            <Chip
                              icon={<EventIcon sx={{ fontSize: '12px !important' }} />}
                              label={option.schedule_count === 1 ? t('Scheduled') : `${option.schedule_count} ${t('schedules')}`}
                              size="small"
                              sx={{ height: 20, fontSize: '11px', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-icon': { ml: 0.5 } }}
                            />
                          )}
                        </Box>
                        <Box sx={{
                          px: 0.75, py: 0.25, borderRadius: '4px',
                          bgcolor: stateConf.bg, color: stateConf.color,
                          fontSize: '11px', fontWeight: 500, lineHeight: 1.4,
                        }}>
                          {t(stateConf.labelKey)}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.vehicle_registration && (
                          <Typography variant="body2" sx={{ ...plateFont, fontWeight: 500, fontSize: '13px' }}>
                            {option.vehicle_registration}
                          </Typography>
                        )}
                        {option.customer_name && (
                          <Typography variant="caption" color="text.secondary">
                            {option.customer_name}
                          </Typography>
                        )}
                        {employeeNames.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {employeeNames.join(', ')}
                          </Typography>
                        )}
                      </Box>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }} noWrap>
                          {option.description.length > 40 ? option.description.slice(0, 40) + '…' : option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('Search by registration, work number, or customer...')}
                  size="small"
                  autoComplete="off"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {maintenanceSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={maintenanceSearchTerm ? t('No maintenances found') : t('Type to search...')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeScheduled}
                  onChange={(e) => setIncludeScheduled(e.target.checked)}
                  size="small"
                />
              }
              label={t('Also show already scheduled')}
              sx={{ mt: 0.5, '& .MuiFormControlLabel-label': { fontSize: '13px', color: 'text.secondary' } }}
            />
            </>
          )}
        </Box>

        {/* Start and End DateTime Row */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Start time')}</Typography>
              <DateTimePicker
                value={formData.startDateTime}
                onChange={(newValue) => handleFieldChange('startDateTime', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    'data-testid': 'schedule-start-time',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                  },
                }}
                ampm={false}
                minutesStep={PICKER_MINUTES}
                format="dd.MM.yyyy HH:mm"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('End time')}</Typography>
              <DateTimePicker
                value={formData.endDateTime}
                onChange={(newValue) => handleFieldChange('endDateTime', newValue)}
                minDateTime={formData.startDateTime ? new Date(formData.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000) : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    'data-testid': 'schedule-end-time',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                  },
                }}
                ampm={false}
                minutesStep={PICKER_MINUTES}
                format="dd.MM.yyyy HH:mm"
              />
            </Box>
          </Box>
        </LocalizationProvider>

        {/* Service Bay */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Service bay')}</Typography>
          <FormControl fullWidth size="small">
              <Select
                value={formData.serviceBayId}
                onChange={(e) => handleFieldChange('serviceBayId', e.target.value)}
                data-testid="schedule-bay-select"
                displayEmpty
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="">
                  <Typography color="text.secondary">{t('Not selected')}</Typography>
                </MenuItem>
                {serviceBays.map((bay) => (
                  <MenuItem key={bay.id} value={bay.id}>
                    {bay.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

        {/* Employees */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Employees')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {employees.map((emp) => (
              <Box
                key={emp.id}
                onClick={() => handleEmployeeToggle(emp.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  px: 1.5,
                  borderRadius: '8px',
                  border: 1,
                  borderColor: formData.employeeIds.includes(emp.id) ? 'secondary.main' : 'divider',
                  bgcolor: formData.employeeIds.includes(emp.id) ? 'rgba(49, 130, 206, 0.08)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: 'secondary.main',
                  },
                }}
              >
                <Checkbox
                  checked={formData.employeeIds.includes(emp.id)}
                  size="small"
                  sx={{ p: 0 }}
                />
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: emp.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 600,
                  }}
                >
                  {emp.initials}
                </Box>
                <Typography sx={{ fontSize: '13px' }}>{emp.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Customer reminder / notification */}
        <ScheduleReminderSection
          {...reminders}
          disabled={loading}
          contactOverride={
            !isEditMode && mode === 'new'
              ? customerMode === 'new'
                ? { phone: newCustomer.phone, email: newCustomer.email }
                : selectedCustomer
                  ? { phone: selectedCustomer.phone, email: selectedCustomer.email }
                  : null // no customer picked yet — a missing-contact warning would mislead
              : null
          }
        />

        {/* Booking warning when about to delete */}
        {isEditMode && deleteConfirm && editingEvent?.has_active_booking && (
          <Alert severity="warning" sx={{ mb: 1, borderRadius: '8px' }}>
            {t('booking_linked_remove_warning')}
          </Alert>
        )}

      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        {/* Delete button - only in edit mode */}
        {isEditMode ? (
          <Button
            onClick={handleDelete}
            variant="outlined"
            color="error"
            disabled={loading || !canSchedule}
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: '10px',
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
            }}
          >
            {deleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete reservation')}</span>}
          </Button>
        ) : (
          <Box />
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={closeScheduleDialog}
            variant="outlined"
            sx={{
              borderRadius: '10px',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              textTransform: 'none',
              px: 2.5,
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            data-testid="schedule-dialog-submit"
            disabled={
              loading ||
              !canSchedule ||
              (!isEditMode && mode === 'existing' && !formData.maintenanceId) ||
              (!isEditMode && mode === 'new' && !newMaintenanceData.registrationNumber)
            }
            sx={{
              borderRadius: '10px',
              bgcolor: 'secondary.main',
              fontWeight: 500,
              textTransform: 'none',
              px: 2.5,
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>

      {/* Extra Lookup Confirmation Dialog */}
      <ExtraLookupConfirmDialog
        open={extraLookupDialogOpen}
        onClose={() => setExtraLookupDialogOpen(false)}
        onConfirm={() => lookupWithConfirm(newMaintenanceData.registrationNumber)}
        quotaInfo={quotaInfo}
        isTrial={quotaInfo?.is_trial || false}
      />
    </Dialog>
  );
}

export default withTranslation()(ScheduleDialog);
